import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchSections, fetchEmergencyContacts } from "../lib/api";
import { Phone, ArrowUpRight, BookOpen, Calculator, Map, ShieldAlert, Sparkles, GraduationCap } from "lucide-react";

const CATEGORY_ICONS = {
    "Introduction": BookOpen, "Emergency": ShieldAlert, "Codes & Conduct": BookOpen,
    "Requirements": BookOpen, "Procedures": BookOpen, "Safety": ShieldAlert,
    "Policies": BookOpen, "Practical": BookOpen, "Training": GraduationCap, "About": Sparkles,
};

export default function Home() {
    const [sections, setSections] = useState([]);
    const [contacts, setContacts] = useState(null);

    useEffect(() => {
        fetchSections().then(setSections).catch(() => {});
        fetchEmergencyContacts().then(setContacts).catch(() => {});
    }, []);

    const grouped = sections.reduce((g, s) => {
        (g[s.category] = g[s.category] || []).push(s); return g;
    }, {});

    return (
        <div className="fade-up">
            {/* Hero */}
            <section className="relative border-b border-gray-200 overflow-hidden">
                {/* Cairngorms background */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.unsplash.com/photo-1500964757637-c85e8a162699?auto=format&fit=crop&w=2400&q=80')"
                    }}
                    aria-hidden="true"
                />
                {/* Overlays for legibility */}
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/40" aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white/70" aria-hidden="true" />

                <div className="relative max-w-5xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="inline-block h-px w-8 bg-[#E57373]" aria-hidden="true" />
                        <div className="text-[11px] uppercase tracking-[0.2em] text-[#E57373] font-semibold">Volunteer Driver Resource</div>
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-[1.05] max-w-3xl" style={{ fontFamily: "Outfit" }}>
                        The BSCC Driver<br />
                        Handbook,<br />
                        <span className="text-[#2C7A85]">now interactive.</span>
                    </h1>
                    <p className="mt-6 text-lg sm:text-xl text-gray-700 max-w-2xl leading-relaxed">
                        Everything a volunteer driver for Badenoch &amp; Strathspey Community Connections needs — emergency procedures, policies, fares, mileage and training — searchable, calculable, and just a question away.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/section/emergency-procedures" className="inline-flex items-center gap-2 bg-[#2C7A85] hover:bg-[#1F5F69] text-white px-5 py-3 rounded-md font-medium transition-colors shadow-sm" data-testid="hero-emergency-cta">
                            <ShieldAlert className="h-4 w-4" /> Emergency Procedures
                        </Link>
                        <Link to="/fare" className="inline-flex items-center gap-2 bg-white/95 backdrop-blur border border-gray-300 hover:border-[#2C7A85] text-gray-900 px-5 py-3 rounded-md font-medium transition-colors" data-testid="hero-fare-cta">
                            <Calculator className="h-4 w-4" /> Calculate a Fare
                        </Link>
                    </div>
                    <div className="mt-8 text-xs text-gray-500">
                        Set against the Cairngorms — the landscape we serve.
                    </div>
                </div>
            </section>

            {/* Emergency strip */}
            {contacts && (
                <section className="border-b border-gray-200" style={{ background: "#FFF4F2" }}>
                    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-6">
                        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-[#B91C1C] grid place-items-center text-white flex-shrink-0">
                                    <Phone className="h-4 w-4" />
                                </div>
                                <div>
                                    <div className="font-semibold text-[#B91C1C]" style={{ fontFamily: "Outfit" }}>Out-of-hours contacts</div>
                                    <div className="text-sm text-gray-700">Call any of these if you have a problem in the evenings or at weekends.</div>
                                </div>
                            </div>
                            <Link to="/emergency" className="inline-flex items-center gap-1 text-sm font-medium text-[#B91C1C] hover:underline" data-testid="see-all-contacts">
                                See all contacts <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                            {contacts.out_of_hours.map((c) => (
                                <a key={c.name} href={`tel:${c.phone.replace(/\s/g, "")}`} className="bg-white border border-red-100 rounded-md px-3 py-2 hover:border-[#B91C1C] transition-colors" data-testid={`contact-${c.name}`}>
                                    <div className="text-[11px] uppercase tracking-widest text-gray-500">{c.name.split(" ")[0]}</div>
                                    <div className="font-medium text-gray-900 text-sm">{c.phone}</div>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Before you drive — featured H&S */}
            <section className="border-b border-gray-200 bg-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold mb-3">Before you drive</div>
                    <Link
                        to="/section/health-safety"
                        className="group grid grid-cols-1 md:grid-cols-[1fr_280px] gap-6 md:gap-8 items-stretch border border-gray-200 rounded-xl overflow-hidden hover:border-[#2C7A85] hover:shadow-md transition-all"
                        data-testid="before-you-drive-card"
                    >
                        <div className="p-6 sm:p-8 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <ShieldAlert className="h-5 w-5 text-[#E57373]" />
                                    <div className="text-sm font-semibold text-gray-900 uppercase tracking-wider" style={{ fontFamily: "Outfit" }}>Health &amp; Safety Guidance</div>
                                </div>
                                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight" style={{ fontFamily: "Outfit" }}>
                                    Eight scenarios every volunteer should know.
                                </h3>
                                <p className="text-[15px] text-gray-600 leading-relaxed mb-4">
                                    First aid, falls, walking aids, wheelchair transfers, dementia, sensory impairments — illustrated guidance for the situations you may face on a journey.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {["Sudden illness", "Falls", "Wheelchair", "Dementia", "Visual", "Hearing"].map(tag => (
                                    <span key={tag} className="inline-block text-xs px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-full text-gray-700">{tag}</span>
                                ))}
                            </div>
                            <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-[#2C7A85] group-hover:gap-2 transition-all">
                                Read the guidance <ArrowUpRight className="h-4 w-4" />
                            </div>
                        </div>
                        <div
                            className="hidden md:block bg-cover bg-center min-h-[260px]"
                            style={{ backgroundImage: "url('/handbook-images/wheelchair.png')" }}
                            aria-hidden="true"
                        />
                    </Link>
                </div>
            </section>

            {/* Tools */}
            <section className="border-b border-gray-200">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold mb-3">Interactive Tools</div>
                    <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-8" style={{ fontFamily: "Outfit" }}>The Handbook, made useful.</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                            { to: "/fare", icon: Calculator, title: "Fare Calculator", desc: "Look up the passenger charge for any number of miles." },
                            { to: "/mileage", icon: Map, title: "Mileage Chart", desc: "Distances between every town we serve." },
                            { to: "/emergency", icon: Phone, title: "Emergency Contacts", desc: "Out-of-hours numbers, ready to tap." },
                        ].map(t => {
                            const Icon = t.icon;
                            return (
                                <Link key={t.to} to={t.to} className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-[#2C7A85] hover:shadow-md transition-all" data-testid={`tool-card-${t.to.replace("/", "")}`}>
                                    <Icon className="h-6 w-6 text-[#2C7A85] mb-4" />
                                    <div className="text-lg font-semibold text-gray-900 mb-1" style={{ fontFamily: "Outfit" }}>{t.title}</div>
                                    <div className="text-[15px] text-gray-600 leading-relaxed">{t.desc}</div>
                                    <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#2C7A85] group-hover:gap-2 transition-all">
                                        Open <ArrowUpRight className="h-4 w-4" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* All sections by category */}
            <section>
                <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-semibold mb-3">Table of Contents</div>
                    <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-8" style={{ fontFamily: "Outfit" }}>Browse the Handbook</h2>
                    <div className="space-y-10">
                        {Object.entries(grouped).map(([cat, items]) => {
                            const Icon = CATEGORY_ICONS[cat] || BookOpen;
                            return (
                                <div key={cat}>
                                    <div className="flex items-center gap-2 mb-4">
                                        <Icon className="h-4 w-4 text-[#E57373]" />
                                        <div className="text-sm font-semibold text-gray-900 uppercase tracking-wider" style={{ fontFamily: "Outfit" }}>{cat}</div>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {items.map(s => (
                                            <Link key={s.id} to={`/section/${s.id}`} className="bg-white border border-gray-200 rounded-md p-4 hover:border-[#2C7A85] hover:shadow-sm transition-all" data-testid={`home-section-${s.id}`}>
                                                <div className="font-medium text-gray-900 mb-1" style={{ fontFamily: "Outfit" }}>{s.title}</div>
                                                <div className="text-sm text-gray-600 leading-relaxed">{s.summary}</div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <footer className="border-t border-gray-200 mt-10 py-10 px-6 text-center text-sm text-gray-500">
                <div>Badenoch &amp; Strathspey Community Connections · Scottish Charity SC029556 · Company 201351</div>
                <div className="mt-1">Office: 01479 810004</div>
            </footer>
        </div>
    );
}
